// src/utils/reportePagosdebancoGenerator.js

import html2pdf from 'html2pdf.js';
import { obtenerDatosEmpresa } from './tenantConfig'; // Ajusta la ruta si es necesario

// ==================== CONFIGURACIÓN DE LA EMPRESA ====================
export const JEAN_INFORMATICO_CONFIG = {
  nombre: "JEAN INFORMATICO C.A.",
  rif: "J-41023119-0",
  direccion: "Calle 30 con Av. 33, C.C. Latin Center P/B, Local 01 - Acarigua, Portuguesa",
  telefono: "0412-5696219 / 0416-7594723",
  whatsapp: "04167594723",
  ciudad: "Acarigua, Estado Portuguesa",
  email: "soportejeaninformatico@gmail.com",
  sitioWeb: "www.jeaninformatico.com.ve",
};

const getEmpresa = () => {
  try {
    const datos = obtenerDatosEmpresa() || {};
    return {
      nombre: (datos.nombreComercial && datos.nombreComercial !== 'MI EMPRESA') 
              ? datos.nombreComercial 
              : JEAN_INFORMATICO_CONFIG.nombre,
      rif: datos.rif || JEAN_INFORMATICO_CONFIG.rif,
      direccion: datos.direccion || JEAN_INFORMATICO_CONFIG.direccion,
      telefono: datos.telefono1 || JEAN_INFORMATICO_CONFIG.telefono,
      email: datos.email || JEAN_INFORMATICO_CONFIG.email,
      logoBase64: datos.logoBase64 || null,
    };
  } catch (err) {
    return { ...JEAN_INFORMATICO_CONFIG, logoBase64: null };
  }
};

// ==================== MANEJO DEL LOGO ====================
let LOGO_BASE64 = '';
let LOGO_MIME = 'image/jpeg';

const detectarMimeDesdeBase64 = (b64) => {
  if (!b64) return 'image/jpeg';
  if (b64.startsWith('/9j/')) return 'image/jpeg';
  if (b64.startsWith('iVBORw0KGgo')) return 'image/png';
  if (b64.startsWith('R0lGOD')) return 'image/gif';
  if (b64.startsWith('UklGR')) return 'image/webp';
  return 'image/jpeg';
};

const limpiarBase64 = (raw) => {
  if (!raw) return '';
  if (raw.includes(',')) return raw.split(',')[1];
  return raw;
};

export const cargarLogoPagos = async () => {
  try {
    const empresa = getEmpresa();
    if (empresa.logoBase64) {
      const limpio = limpiarBase64(empresa.logoBase64);
      if (limpio && limpio.length > 50) {
        LOGO_BASE64 = limpio;
        LOGO_MIME = detectarMimeDesdeBase64(limpio);
        return LOGO_BASE64;
      }
    }
    const response = await fetch('/logo-jean.jpg'); // Ajusta el nombre de tu logo
    if (!response.ok) { LOGO_BASE64 = ''; return ''; }
    const blob = await response.blob();
    LOGO_MIME = blob.type || 'image/jpeg';

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        LOGO_BASE64 = limpiarBase64(reader.result);
        resolve(LOGO_BASE64);
      };
      reader.onerror = () => { LOGO_BASE64 = ''; resolve(''); };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    LOGO_BASE64 = '';
    return '';
  }
};

export const getLogoSrcPagos = () => LOGO_BASE64 ? `data:${LOGO_MIME};base64,${LOGO_BASE64}` : '';

// ==================== MAPEO DE BANCOS ====================
const BANCOS_CODIGOS = {
  '0102': 'Banco de Venezuela', '0104': 'Venezolano de Crédito', '0105': 'Mercantil',
  '0108': 'Provincial', '0114': 'Bancaribe', '0115': 'Exterior', '0128': 'Banco Caroní',
  '0134': 'Banesco', '0137': 'Sofitasa', '0138': 'Banco Plaza', '0146': 'Bangente',
  '0151': 'BFC Banco Fondo Común', '0156': '100% Banco', '0157': 'DelSur',
  '0163': 'Banco del Tesoro', '0166': 'Banco Agrícola de Venezuela', '0168': 'Bancrecer',
  '0169': 'Mi Banco', '0171': 'Banco Activo', '0172': 'Bancamiga', '0174': 'Banplus',
  '0175': 'Bicentenario', '0177': 'Banfanb', '0191': 'BNC Banco Nacional de Crédito'
};

const nombreBanco = (codigo) => {
  if (!codigo) return '—';
  return BANCOS_CODIGOS[codigo] || codigo;
};

// ==================== UTILIDADES ====================
export const formatearFechaReporte = (fechaISO) => {
  if (!fechaISO) return 'No disponible';
  try {
    const fecha = new Date(fechaISO);
    return fecha.toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return fechaISO; }
};

export const formatearMonto = (monto, moneda = 'USD') => {
  const num = Number(monto) || 0;
  return moneda === 'USD' 
    ? `$${num.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : `Bs. ${num.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// ==================== GENERADOR DE HTML ====================
export const generarHTMLReportePagos = (pagos, titulo = 'REPORTE DE PAGOS', filtros = {}) => {
  const empresa = getEmpresa();
  const fechaActual = new Date();
  const logoSrc = getLogoSrcPagos();

  // 1. Calcular Totales Globales
  const totalUsd = pagos.reduce((sum, p) => sum + (Number(p.montoUsd) || 0), 0);
  const totalBs = pagos.reduce((sum, p) => sum + (Number(p.montoBs) || 0), 0);

  // 2. Agrupar por Cuenta
  const grupos = {};
  pagos.forEach(p => {
    const key = p.numeroCuenta || 'sin_cuenta';
    if (!grupos[key]) {
      grupos[key] = {
        numeroCuenta: p.numeroCuenta,
        bancoNombre: p.bancoNombre,
        bancoCodigo: p.bancoCodigo,
        titularNombreCompleto: p.titularNombreCompleto,
        titularDocumento: p.titularDocumento,
        titularTelefono: p.titularTelefono,
        pagos: [],
        totalUsd: 0,
        totalBs: 0
      };
    }
    grupos[key].pagos.push(p);
    grupos[key].totalUsd += (Number(p.montoUsd) || 0);
    grupos[key].totalBs += (Number(p.montoBs) || 0);
  });

  const gruposArray = Object.values(grupos);

  // Filtros aplicados en texto (Opcional)
  let textoFiltros = '';
  if (filtros.desde || filtros.hasta) {
    textoFiltros += `Período: ${filtros.desde || 'Inicio'} al ${filtros.hasta || 'Fin'} | `;
  }
  if (filtros.metodo && filtros.metodo !== 'all') {
    textoFiltros += `Método: ${filtros.metodo.toUpperCase()} | `;
  }

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>${titulo}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Arial', 'Helvetica', sans-serif; background: white; padding: 10px; color: #333; }
    .reporte { max-width: 900px; margin: 0 auto; }

    /* HEADER */
    .header { display: flex; align-items: center; justify-content: space-between; border-bottom: 2px solid #333; padding-bottom: 8px; margin-bottom: 10px; }
    .logo-img { max-height: 50px; max-width: 100px; object-fit: contain; }
    .header-center { text-align: center; flex: 1; }
    .empresa-nombre { font-size: 15px; font-weight: bold; text-transform: uppercase; }
    .empresa-datos { font-size: 8px; color: #555; margin-top: 2px; }
    
    /* TITULO */
    .titulo-box { text-align: center; margin-bottom: 15px; }
    .titulo-box h1 { font-size: 14px; color: #1a4d8c; text-transform: uppercase; }
    .titulo-box p { font-size: 8px; color: #666; margin-top: 2px; }
    .titulo-box .filtros { font-size: 7px; color: #888; font-style: italic; margin-top: 3px; }

    /* BARRA DE TOTALES GLOBALES */
    .totales-globales {
      background: #6d28d9; color: white; border-radius: 8px;
      padding: 10px 15px; margin-bottom: 15px;
      display: flex; justify-content: space-between; align-items: center;
    }
    .totales-globales .label { font-size: 9px; font-weight: bold; text-transform: uppercase; opacity: 0.8; }
    .totales-globales .monto { font-size: 16px; font-weight: bold; }
    .totales-globales .monto-bs { font-size: 11px; opacity: 0.9; }

    /* CONTENEDOR DE GRUPO (CUENTA) */
    .grupo {
      border: 1px solid #ddd; border-radius: 8px; margin-bottom: 15px;
      page-break-inside: avoid; /* Evita que se corte a la mitad en el PDF */
      overflow: hidden;
    }
    .grupo-header {
      background: #eff6ff; padding: 8px 12px;
      display: flex; justify-content: space-between; align-items: center;
      border-bottom: 1px solid #bfdbfe;
    }
    .grupo-info { display: flex; flex-direction: column; }
    .grupo-titulo { font-size: 11px; font-weight: bold; color: #1e40af; text-transform: uppercase; }
    .grupo-cuenta { font-size: 9px; font-family: monospace; color: #3b82f6; }
    .grupo-titular { font-size: 9px; color: #4b5563; margin-top: 2px; }
    
    .grupo-subtotales { text-align: right; background: white; padding: 4px 8px; border-radius: 6px; border: 1px solid #bfdbfe; }
    .grupo-subtotales .lbl { font-size: 8px; font-weight: bold; color: #3b82f6; text-transform: uppercase; }
    .grupo-subtotales .val { font-size: 12px; font-weight: bold; color: #059669; }
    .grupo-subtotales .val-bs { font-size: 9px; color: #6b7280; }

    /* TABLA INTERNA */
    table { width: 100%; border-collapse: collapse; font-size: 8px; }
    th { background: #f9fafb; padding: 5px; text-align: left; border-bottom: 1px solid #e5e7eb; color: #6b7280; text-transform: uppercase; }
    td { padding: 6px 5px; border-bottom: 1px solid #f3f4f6; vertical-align: top; }
    .text-right { text-align: right; }
    .text-center { text-align: center; }
    .font-bold { font-weight: bold; }
    
    /* ESTILOS PAGO MOVIL */
    .pm-detalle { font-size: 7px; color: #4338ca; margin-top: 3px; background: #eef2ff; padding: 3px 5px; border-radius: 4px; display: block; line-height: 1.2; }
    .pm-tag { background: #e0e7ff; color: #3730a3; padding: 1px 3px; border-radius: 2px; font-size: 7px; font-weight: bold; }

    .footer { margin-top: 20px; font-size: 7px; text-align: center; color: #888; border-top: 1px solid #eee; padding-top: 5px; }

    @media print {
      @page { margin: 0.5cm; size: portrait; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <div class="reporte" id="reporte-pdf">
    
    <div class="header">
      <div style="width: 100px;">${logoSrc ? `<img src="${logoSrc}" class="logo-img" alt="Logo">` : ''}</div>
      <div class="header-center">
        <div class="empresa-nombre">${empresa.nombre}</div>
        <div class="empresa-datos">RIF: ${empresa.rif} | Tel: ${empresa.telefono}<br>${empresa.direccion}</div>
      </div>
      <div style="width: 100px; text-align: right;">${logoSrc ? `<img src="${logoSrc}" class="logo-img" alt="Logo">` : ''}</div>
    </div>

    <div class="titulo-box">
      <h1>${titulo}</h1>
      <p>Generado el: ${formatearFechaReporte(fechaActual.toISOString())}</p>
      ${textoFiltros ? `<div class="filtros">${textoFiltros}</div>` : ''}
    </div>

    <!-- TOTALES -->
    <div class="totales-globales">
      <div>
        <div class="label">Total Pagos</div>
        <div class="monto">${pagos.length}</div>
      </div>
      <div style="text-align: right;">
        <div class="label">Total Recaudado (USD)</div>
        <div class="monto">${formatearMonto(totalUsd, 'USD')}</div>
      </div>
      <div style="text-align: right;">
        <div class="label">Total Recaudado (Bs)</div>
        <div class="monto-bs">${formatearMonto(totalBs, 'VES')}</div>
      </div>
    </div>

    <!-- LISTA AGRUPADA POR CUENTA -->
    ${gruposArray.length > 0 ? gruposArray.map(g => `
      <div class="grupo">
        <div class="grupo-header">
          <div class="grupo-info">
            <div class="grupo-titulo">${g.bancoNombre} (${g.bancoCodigo})</div>
            <div class="grupo-cuenta">${g.numeroCuenta}</div>
            <div class="grupo-titular">Titular: <span class="font-bold">${g.titularNombreCompleto}</span> | ${g.titularDocumento} · ${g.titularTelefono}</div>
          </div>
          <div class="grupo-subtotales">
            <div class="lbl">Total en esta cuenta</div>
            <div class="val">${formatearMonto(g.totalUsd, 'USD')}</div>
            <div class="val-bs">${formatearMonto(g.totalBs, 'VES')}</div>
          </div>
        </div>
        <table>
          <colgroup>
            <col style="width: 12%;">
            <col style="width: 15%;">
            <col style="width: 12%;">
            <col style="width: 27%;">
            <col style="width: 16%;">
            <col style="width: 18%;">
          </colgroup>
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Método</th>
              <th>Documento</th>
              <th>Referencia / PM</th>
              <th class="text-right">Monto USD</th>
              <th class="text-right">Monto Bs</th>
            </tr>
          </thead>
          <tbody>
            ${g.pagos.map(p => `
              <tr>
                <td>${new Date(p.fechaPago).toLocaleDateString('es-VE')}</td>
                <td><span class="font-bold">${p.metodoPago ? p.metodoPago.replace('_', ' ').toUpperCase() : '—'}</span></td>
                <td>#${p.idDocumentoPago}</td>
                <td>
                  ${p.referencia || '—'}
                  ${p.pmCodigoOperacion ? `
                    <div class="pm-detalle">
                      <span class="pm-tag">PM</span> <b>${p.pmCodigoOperacion}</b><br>
                      Emisor: ${nombreBanco(p.pmBancoEmisorCodigo)} (${p.pmBancoEmisorCodigo}) | 
                      Receptor: ${nombreBanco(p.pmBancoReceptorCodigo)} (${p.pmBancoReceptorCodigo})<br>
                      Tel: ${p.pmTelefonoEmisor || '—'} | Doc: ${p.pmDocumentoEmisor || '—'}
                    </div>
                  ` : ''}
                </td>
                <td class="text-right font-bold" style="color: #059669;">${formatearMonto(p.montoUsd, 'USD')}</td>
                <td class="text-right">${formatearMonto(p.montoBs, 'VES')}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `).join('') : '<div style="padding: 20px; text-align: center; border: 1px solid #ddd;">No hay pagos para mostrar</div>'}

    <div class="footer">
      ${empresa.nombre} • RIF: ${empresa.rif} • Sistema de Gestión
    </div>
  </div>
</body>
</html>`;
};

// ==================== DESCARGAR PDF ====================
export const descargarPDFPagos = async (pagos, titulo = 'REPORTE DE PAGOS', filtros = {}) => {
  if (!pagos || pagos.length === 0) {
    alert('No hay pagos para generar el reporte');
    return false;
  }

  try {
    LOGO_BASE64 = '';
    LOGO_MIME = 'image/jpeg';
    await cargarLogoPagos(); // Cargamos el logo antes de generar el HTML

    const htmlReporte = generarHTMLReportePagos(pagos, titulo, filtros);

    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = '800px';
    container.style.background = '#fff';
    container.innerHTML = htmlReporte;
    document.body.appendChild(container);

    const elemento = container.querySelector('#reporte-pdf');
    const fecha = new Date().toISOString().split('T')[0];
    const nombreArchivo = `Reporte_Pagos_${fecha}.pdf`;

    const opciones = {
      margin: [10, 10, 10, 10], // Márgenes
      filename: nombreArchivo,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }, // Vertical
      pagebreak: { mode: ['css', 'legacy'], avoid: ['.grupo', 'tr'] } // Evita cortar a la mitad
    };

    await html2pdf().set(opciones).from(elemento).save();
    document.body.removeChild(container);
    return true;
  } catch (error) {
    console.error('Error al generar PDF:', error);
    alert('Error al generar el PDF: ' + error.message);
    return false;
  }
};

// ==================== IMPRIMIR ====================
export const imprimirReportePagos = async (pagos, titulo = 'REPORTE DE PAGOS', filtros = {}) => {
  if (!pagos || pagos.length === 0) return false;
  try {
    await cargarLogoPagos();
    const htmlReporte = generarHTMLReportePagos(pagos, titulo, filtros);
    const ventana = window.open('', '_blank', 'width=1100,height=700');
    if (!ventana) return false;
    ventana.document.write(htmlReporte);
    ventana.document.close();
    ventana.focus();
    return true;
  } catch (error) {
    return false;
  }
};

export default {
  cargarLogoPagos,
  getLogoSrcPagos,
  generarHTMLReportePagos,
  descargarPDFPagos,
  imprimirReportePagos
};